// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box } from '@mantine/core';
import { useMantineTheme } from '@mantine/core';
import { Stack, Group, Center, Container } from '@/components/ui/layout';
import { Title, Text, Button, Anchor, Badge } from '@/components/ui/core';
import { TextInput, PasswordInput, Checkbox } from '@/components/ui/inputs';
import { IconMail, IconLock, IconArrowLeft, IconBolt } from '@tabler/icons-react';
import { FadeIn, SlideIn } from '@/components/animations';
import { NeonCard, SocialLoginButtons } from '../components';

export default function LoginDarkMode() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const theme = useMantineTheme();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Login attempt:', { email, password, rememberMe });
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #0f0f1a 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Grid background */}
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(0, 245, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 245, 255, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          pointerEvents: 'none',
        }}
      />

      {/* Glow effects */}
      <Box
        style={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: 300,
          height: 300,
          background: 'radial-gradient(circle, rgba(0, 245, 255, 0.15) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        style={{
          position: 'absolute',
          bottom: '20%',
          right: '10%',
          width: 250,
          height: 250,
          background: 'radial-gradient(circle, rgba(191, 0, 255, 0.15) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />

      <Center mih="100vh" p="md" style={{ position: 'relative', zIndex: 1 }}>
        <Container size="xs" w="100%">
          <SlideIn direction="up" delay={100}>
            <NeonCard color="cyan" animated glass>
              <Stack gap="lg">
                {/* Back button */}
                <Button
                  component={Link}
                  to="/app/demo/auth-pages/login"
                  variant="subtle"
                  size="xs"
                  leftSection={<IconArrowLeft size={14} />}
                  w="fit-content"
                  c="cyan"
                >
                  Back to variants
                </Button>

                {/* Header */}
                <FadeIn delay={200}>
                  <Stack align="center" gap="xs">
                    <Badge
                      leftSection={<IconBolt size={12} />}
                      variant="filled"
                      color="cyan"
                      size="lg"
                      style={{
                        boxShadow: '0 0 20px rgba(0, 245, 255, 0.5)',
                      }}
                    >
                      Cyberpunk Login
                    </Badge>
                    <Title order={2} ta="center" mt="md" c="white">
                      ACCESS TERMINAL
                    </Title>
                    <Text size="sm" ta="center" c="dimmed">
                      Initialize authentication sequence
                    </Text>
                  </Stack>
                </FadeIn>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  <Stack gap="md">
                    <FadeIn delay={300}>
                      <TextInput
                        label="Email"
                        placeholder="user@system.net"
                        leftSection={<IconMail size={16} />}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        size="md"
                        radius="md"
                        styles={{
                          input: {
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            borderColor: 'rgba(0, 245, 255, 0.3)',
                            color: 'white',
                            '&:focus': {
                              borderColor: '#00f5ff',
                              boxShadow: '0 0 10px rgba(0, 245, 255, 0.3)',
                            },
                          },
                          label: { color: '#00f5ff' },
                        }}
                      />
                    </FadeIn>

                    <FadeIn delay={400}>
                      <PasswordInput
                        label="Password"
                        placeholder="••••••••"
                        leftSection={<IconLock size={16} />}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        size="md"
                        radius="md"
                        styles={{
                          input: {
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            borderColor: 'rgba(0, 245, 255, 0.3)',
                            color: 'white',
                            '&:focus': {
                              borderColor: '#00f5ff',
                              boxShadow: '0 0 10px rgba(0, 245, 255, 0.3)',
                            },
                          },
                          label: { color: '#00f5ff' },
                        }}
                      />
                    </FadeIn>

                    <FadeIn delay={500}>
                      <Group justify="space-between">
                        <Checkbox
                          label="Remember device"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.currentTarget.checked)}
                          styles={{
                            label: { color: 'white' },
                          }}
                        />
                        <Anchor
                          component={Link}
                          to="/app/demo/auth-pages/forgot-password/neon"
                          size="sm"
                          c="cyan"
                        >
                          Recovery protocol?
                        </Anchor>
                      </Group>
                    </FadeIn>

                    <FadeIn delay={600}>
                      <Button
                        type="submit"
                        fullWidth
                        size="md"
                        radius="md"
                        color="cyan"
                        variant="filled"
                        style={{
                          boxShadow: '0 0 20px rgba(0, 245, 255, 0.4)',
                        }}
                      >
                        AUTHENTICATE
                      </Button>
                    </FadeIn>
                  </Stack>
                </form>

                {/* Social Login */}
                <FadeIn delay={700}>
                  <SocialLoginButtons
                    providers={['google', 'github']}
                    layout="horizontal"
                    variant="outline"
                    dividerLabel="or link account"
                  />
                </FadeIn>

                {/* Register Link */}
                <FadeIn delay={800}>
                  <Text ta="center" size="sm" c="dimmed">
                    New user?{' '}
                    <Anchor component={Link} to="/app/demo/auth-pages/register/neon" c="cyan" fw={500}>
                      Create identity
                    </Anchor>
                  </Text>
                </FadeIn>
              </Stack>
            </NeonCard>
          </SlideIn>
        </Container>
      </Center>
    </Box>
  );
}
